import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  applyTelegramColors,
  getTelegramUser,
  getTheme,
  getThemePreference,
  saveThemePreference,
  isTelegram,
  type ThemePreference,
} from '../services/telegram';
import { loadTwa } from '../services/twa';
import { ensureWebProfile, renameWebProfile } from '../services/web-profile';
import { applyDesign, getDesign, saveDesign, getCategoryStyle, saveCategoryStyle, applyCategoryStyle, type DesignId, type CategoryStyle } from '../services/design';
import { applyUiMode, getUiMode, saveUiMode, type UiMode } from '../services/mode';

// Универсальный пользователь (barter-024, web-first): в Mini App — данные
// Telegram, в обычном браузере — локальный веб-профиль (Dexie + /web-session).
// Потребители работают с user одинаково в обоих режимах; userReady=false,
// пока режим определяется (подгрузка SDK ТГ или регистрация веб-профиля).

export interface AppUser {
  id: number;
  firstName: string;
  lastName: string;
  username: string;
  source: 'telegram' | 'web';
}

interface UserContextValue {
  user: AppUser | null;
  /** false, пока определяется режим (ТГ или веб-профиль) */
  userReady: boolean;
  /** Переименование веб-профиля (для ТГ-пользователя — no-op) */
  renameUser: (name: string) => Promise<void>;
  theme: 'light' | 'dark';
  /** Выбор пользователя: dark/light/system (сам по себе не «итоговая» тема) */
  themePreference: ThemePreference;
  setThemePreference: (pref: ThemePreference) => void;
  isTelegram: boolean;
  design: DesignId;
  setDesign: (id: DesignId) => void;
  categoryStyle: CategoryStyle;
  setCategoryStyle: (style: CategoryStyle) => void;
  /** Простой (дефолт) / полный режим интерфейса */
  uiMode: UiMode;
  setUiMode: (mode: UiMode) => void;
}

const UserContext = createContext<UserContextValue>({
  user: null,
  userReady: false,
  renameUser: async () => {},
  theme: 'dark',
  themePreference: 'dark',
  setThemePreference: () => {},
  isTelegram: false,
  design: 'ukraine',
  setDesign: () => {},
  categoryStyle: 'photo',
  setCategoryStyle: () => {},
  uiMode: 'simple',
  setUiMode: () => {},
});

export function UserProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AppUser | null>(null);
  const [userReady, setUserReady] = useState(false);
  const [telegram, setTelegram] = useState(false);
  const [themePreference, setThemePreferenceState] = useState<ThemePreference>(getThemePreference);
  const [theme, setTheme] = useState<'light' | 'dark'>(() => getTheme(getThemePreference()));
  const [design, setDesignState] = useState<DesignId>(getDesign);
  const [categoryStyle, setCategoryStyleState] = useState<CategoryStyle>(getCategoryStyle);
  const [uiMode, setUiModeState] = useState<UiMode>(getUiMode);

  // Определение пользователя: ТГ (если SDK загрузился и дал user) или веб-профиль
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const app = await loadTwa();
      if (cancelled) return;
      if (app) {
        setTelegram(isTelegram());
        const tgUser = getTelegramUser();
        if (tgUser) {
          setUser({ ...tgUser, source: 'telegram' });
          setUserReady(true);
          return;
        }
      }
      try {
        const profile = await ensureWebProfile();
        if (cancelled) return;
        setUser({
          id: profile.id,
          firstName: profile.name || `user${profile.id}`,
          lastName: '',
          username: '',
          source: 'web',
        });
      } catch {
        // нет сети и профиля нет — страницы профиля предложат повторить позже
      } finally {
        if (!cancelled) setUserReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    applyDesign(design);
  }, [design]);

  useEffect(() => {
    applyUiMode(uiMode);
  }, [uiMode]);

  useEffect(() => {
    applyCategoryStyle(categoryStyle);
  }, [categoryStyle]);

  // Цвета шапки/фона Telegram должны соответствовать выбранному дизайну
  useEffect(() => {
    applyTelegramColors(design, theme);
  }, [design, theme]);

  // Смена темы Telegram/ОС меняет приложение только в режиме «Системна»
  useEffect(() => {
    let off: (() => void) | null = null;
    void loadTwa().then((app) => {
      if (!app) return;
      try {
        const handler = () => {
          if (getThemePreference() === 'system') setTheme(getTheme('system'));
        };
        app.onEvent('themeChanged', handler);
        off = () => {
          try {
            app.offEvent('themeChanged', handler);
          } catch {}
        };
      } catch {}
    });
    return () => {
      off?.();
    };
  }, []);

  const renameUser = useCallback(
    async (name: string) => {
      const trimmed = name.trim().slice(0, 40);
      if (!trimmed || user?.source !== 'web') return;
      await renameWebProfile(trimmed);
      setUser({ ...user, firstName: trimmed });
    },
    [user],
  );

  const value = useMemo<UserContextValue>(
    () => ({
      user,
      userReady,
      renameUser,
      theme,
      themePreference,
      setThemePreference: (pref: ThemePreference) => {
        saveThemePreference(pref);
        setThemePreferenceState(pref);
        setTheme(getTheme(pref));
      },
      isTelegram: telegram,
      design,
      setDesign: (id: DesignId) => {
        saveDesign(id);
        setDesignState(id);
      },
      categoryStyle,
      setCategoryStyle: (style: CategoryStyle) => {
        saveCategoryStyle(style);
        setCategoryStyleState(style);
      },
      uiMode,
      setUiMode: (mode: UiMode) => {
        saveUiMode(mode);
        setUiModeState(mode);
      },
    }),
    [user, userReady, renameUser, theme, themePreference, telegram, design, categoryStyle, uiMode],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser() {
  return useContext(UserContext);
}
